import { z } from 'zod';

export const PermissionGroupEnum = z.enum([
  'roles',
  'users',
  'tenants',
  'organizations',
  'corpOrganizations',
  'trainers',
  'tags',
  'categories',
  'configurables',
  'privacyPolicy',
  'meals',
  'plan',
  'events',
  'schedule',
  'notificationCenter',
  'billing',
  'memberships',
  'prospects',
  'desk',
  'businessOverview',
  'stats',
  'premiumContent',
  'questionnaires',
  // F1 (platform-user-class): fallback group for backend keys that are not
  // part of the FE role-editing catalog — they still count in hasPermission.
  'other',
]);

export type ApiPermissionGroup = z.infer<typeof PermissionGroupEnum>;

export const PermissionEnum = z.enum([
  'roles_create',
  'roles_read',
  'roles_update',
  'roles_delete',
  'users_create',
  'users_read',
  'users_update',
  'users_delete',
  'tenants_create',
  'tenants_read',
  'tenants_update',
  'tenants_delete',
  'organizations_create',
  'organizations_read',
  'organizations_update',
  'organizations_delete',
  'org_rebrand_manage',
  'public_page_manage',
  'corp_organizations_create',
  'corp_organizations_read',
  'corp_organizations_update',
  'corp_organizations_delete',
  'trainers_create',
  'trainers_read',
  'trainers_update',
  'trainers_delete',
  'trainer_clients_assign',
  'trainer_fees_manage',
  'trainer_earnings_read',
  'locker_keys_manage',
  'day_notes_manage',
  'health_screening_release',
  'configurables_create',
  'configurables_read',
  'configurables_delete',
  'configurables_update',
  'categories_create',
  'categories_read',
  'categories_delete',
  'categories_update',
  'tags_create',
  'tags_read',
  'tags_update',
  'tags_delete',
  'exercises_create',
  'exercises_read',
  'exercises_update',
  'exercises_delete',
  'privacy_policy_create',
  'privacy_policy_read',
  'privacy_policy_update',
  'privacy_policy_delete',
  'workouts_create',
  'workouts_read',
  'workouts_update',
  'workouts_delete',
  'meals_create',
  'meals_read',
  'meals_update',
  'meals_delete',
  'plan_create',
  'plan_read',
  'plan_update',
  'plan_delete',
  'programs_create',
  'programs_read',
  'programs_update',
  'programs_delete',
  'education_create',
  'education_read',
  'education_update',
  'education_delete',
  'events_create',
  'events_read',
  'events_delete',
  'events_update',
  'schedule_create',
  'schedule_read',
  'schedule_delete',
  'schedule_update',
  'schedule_self_update',
  'schedule_copy',
  // MV2-394 — trainer-side event CRUD is staff-only; the `bookings_*` set is
  // what a client holds for their own seat
  'booking_events_create',
  'booking_events_read',
  'booking_events_update',
  'booking_events_delete',
  // reusable "class definition" a booking event can be generated from —
  // managing templates is a separate right from managing ad-hoc events, but
  // actually generating an event from one still uses `booking_events_create`
  'booking_event_templates_create',
  'booking_event_templates_read',
  'booking_event_templates_update',
  'booking_event_templates_delete',
  'bookings_create',
  'bookings_read',
  'bookings_manage',
  'bookings_cancel',
  'bookings_waitlist',
  'notification_center_create',
  'notification_center_read',
  'notification_center_update',
  'notification_center_delete',
  'billing_read',
  'billing_manage',
  'billing_discounts_manage',
  // Nivo 4 — manual finance entries and the monthly net result. Beside
  // `finance_stats_read` in the `billing` group: same screen, same money.
  'finance_entries_read',
  'finance_entries_manage',
  'finance_stats_read',
  // W0.1 — gym memberships (money the ORGANISATION collects). Deliberately
  // NOT in the `billing` group: that one is the org's own subscription to
  // MFW, and the two have been confused before.
  'memberships_create',
  'memberships_update',
  'memberships_delete',
  'client_memberships_manage',
  'membership_payments_manage',
  'membership_checkins_manage',
  'prospects_create',
  'prospects_read',
  'prospects_update',
  'prospects_delete',
  'prospects_tickets_manage',
  'desk_products_manage',
  'desk_sales_manage',
  'business_overview_read',
  'stats_clients_read',
  'stats_org_read',
  'stats_platform_read',
  'stats_premium_read',
  'premium_content_manage',
  'questionnaires_create',
  'questionnaires_read',
  'questionnaires_update',
  'questionnaires_delete',
  // F1 (platform-user-class): backend keys that are intentionally NOT in the
  // role-editing catalog (Permissions array below) but exist on the server —
  // platform users receive them via the P5 bypass and hasPermission() must
  // be able to reference them.
  'platform_configurables_create',
  'platform_configurables_read',
  'platform_configurables_update',
  'platform_configurables_delete',
  'platform_categories_create',
  'platform_categories_read',
  'platform_categories_update',
  'platform_categories_delete',
  'platform_tags_create',
  'platform_tags_read',
  'platform_tags_update',
  'platform_tags_delete',
  'subscriptions_create',
  'subscriptions_read',
  'subscriptions_update',
  'subscriptions_delete',
  'program_marketplace_share',
  'program_public_share',
  'workout_marketplace_share',
  'workout_public_share',
]);

export type ApiPermission = z.infer<typeof PermissionEnum>;

export const PermissionSchema = z.object({
  group: PermissionGroupEnum,
  permission: PermissionEnum,
});

export type UiPermission = z.infer<typeof PermissionSchema>;

export const Permissions = [
  {
    group: 'events',
    permission: 'events_create',
  },
  {
    group: 'events',
    permission: 'events_delete',
  },
  {
    group: 'events',
    permission: 'events_read',
  },
  {
    group: 'events',
    permission: 'events_update',
  },
  {
    group: 'schedule',
    permission: 'schedule_create',
  },
  {
    group: 'schedule',
    permission: 'schedule_delete',
  },
  {
    group: 'schedule',
    permission: 'schedule_read',
  },
  {
    group: 'schedule',
    permission: 'schedule_update',
  },
  {
    group: 'schedule',
    permission: 'schedule_self_update',
  },
  {
    group: 'schedule',
    permission: 'schedule_copy',
  },
  {
    group: 'roles',
    permission: 'roles_create',
  },
  {
    group: 'roles',
    permission: 'roles_delete',
  },
  {
    group: 'roles',
    permission: 'roles_read',
  },
  {
    group: 'roles',
    permission: 'roles_update',
  },
  {
    group: 'users',
    permission: 'users_create',
  },
  {
    group: 'users',
    permission: 'users_delete',
  },
  {
    group: 'users',
    permission: 'users_read',
  },
  {
    group: 'users',
    permission: 'users_update',
  },
  {
    group: 'tenants',
    permission: 'tenants_create',
  },
  {
    group: 'tenants',
    permission: 'tenants_delete',
  },
  {
    group: 'tenants',
    permission: 'tenants_read',
  },
  {
    group: 'tenants',
    permission: 'tenants_update',
  },
  {
    group: 'trainers',
    permission: 'trainers_create',
  },
  {
    group: 'trainers',
    permission: 'trainers_delete',
  },
  {
    group: 'trainers',
    permission: 'trainers_read',
  },
  {
    group: 'trainers',
    permission: 'trainers_update',
  },
  {
    group: 'trainers',
    permission: 'trainer_clients_assign',
  },
  {
    group: 'trainers',
    permission: 'trainer_fees_manage',
  },
  {
    group: 'trainers',
    permission: 'trainer_earnings_read',
  },
  {
    group: 'organizations',
    permission: 'organizations_create',
  },
  {
    group: 'organizations',
    permission: 'organizations_delete',
  },
  {
    group: 'organizations',
    permission: 'organizations_read',
  },
  {
    group: 'organizations',
    permission: 'organizations_update',
  },
  {
    group: 'organizations',
    permission: 'org_rebrand_manage',
  },
  {
    group: 'organizations',
    permission: 'public_page_manage',
  },
  {
    group: 'corpOrganizations',
    permission: 'corp_organizations_create',
  },
  {
    group: 'corpOrganizations',
    permission: 'corp_organizations_read',
  },
  {
    group: 'corpOrganizations',
    permission: 'corp_organizations_update',
  },
  {
    group: 'corpOrganizations',
    permission: 'corp_organizations_delete',
  },
  {
    group: 'configurables',
    permission: 'configurables_create',
  },
  {
    group: 'configurables',
    permission: 'configurables_read',
  },
  {
    group: 'configurables',
    permission: 'configurables_update',
  },
  {
    group: 'configurables',
    permission: 'configurables_delete',
  },
  {
    group: 'categories',
    permission: 'categories_create',
  },
  {
    group: 'categories',
    permission: 'categories_read',
  },
  {
    group: 'categories',
    permission: 'categories_update',
  },
  {
    group: 'categories',
    permission: 'categories_delete',
  },
  {
    group: 'tags',
    permission: 'tags_create',
  },
  {
    group: 'tags',
    permission: 'tags_read',
  },
  {
    group: 'tags',
    permission: 'tags_update',
  },
  {
    group: 'tags',
    permission: 'tags_delete',
  },
  {
    group: 'workouts',
    permission: 'workouts_update',
  },
  {
    group: 'workouts',
    permission: 'workouts_create',
  },
  {
    group: 'workouts',
    permission: 'workouts_delete',
  },
  {
    group: 'workouts',
    permission: 'workouts_read',
  },
  {
    group: 'meals',
    permission: 'meals_update',
  },
  {
    group: 'meals',
    permission: 'meals_create',
  },
  {
    group: 'meals',
    permission: 'meals_delete',
  },
  {
    group: 'meals',
    permission: 'meals_read',
  },
  {
    group: 'plan',
    permission: 'plan_update',
  },
  {
    group: 'plan',
    permission: 'plan_create',
  },
  {
    group: 'plan',
    permission: 'plan_delete',
  },
  {
    group: 'plan',
    permission: 'plan_read',
  },
  {
    group: 'programs',
    permission: 'programs_update',
  },
  {
    group: 'programs',
    permission: 'programs_create',
  },
  {
    group: 'programs',
    permission: 'programs_delete',
  },
  {
    group: 'programs',
    permission: 'programs_read',
  },
  {
    group: 'education',
    permission: 'education_create',
  },
  {
    group: 'education',
    permission: 'education_read',
  },
  {
    group: 'education',
    permission: 'education_update',
  },
  {
    group: 'education',
    permission: 'education_delete',
  },
  {
    group: 'exercises',
    permission: 'exercises_update',
  },
  {
    group: 'exercises',
    permission: 'exercises_create',
  },
  {
    group: 'exercises',
    permission: 'exercises_delete',
  },
  {
    group: 'exercises',
    permission: 'exercises_read',
  },
  {
    group: 'privacyPolicy',
    permission: 'privacy_policy_create',
  },
  {
    group: 'privacyPolicy',
    permission: 'privacy_policy_read',
  },
  {
    group: 'privacyPolicy',
    permission: 'privacy_policy_update',
  },
  {
    group: 'privacyPolicy',
    permission: 'privacy_policy_delete',
  },
  {
    group: 'notificationCenter',
    permission: 'notification_center_create',
  },
  {
    group: 'notificationCenter',
    permission: 'notification_center_read',
  },
  {
    group: 'notificationCenter',
    permission: 'notification_center_update',
  },
  {
    group: 'notificationCenter',
    permission: 'notification_center_delete',
  },
  {
    group: 'billing',
    permission: 'billing_read',
  },
  {
    group: 'billing',
    permission: 'billing_manage',
  },
  {
    group: 'billing',
    permission: 'billing_discounts_manage',
  },
  {
    group: 'billing',
    permission: 'finance_stats_read',
  },
  {
    group: 'billing',
    permission: 'finance_entries_read',
  },
  {
    group: 'billing',
    permission: 'finance_entries_manage',
  },
  {
    group: 'memberships',
    permission: 'memberships_create',
  },
  {
    group: 'memberships',
    permission: 'memberships_update',
  },
  {
    group: 'memberships',
    permission: 'memberships_delete',
  },
  {
    group: 'memberships',
    permission: 'client_memberships_manage',
  },
  {
    group: 'memberships',
    permission: 'membership_payments_manage',
  },
  {
    group: 'memberships',
    permission: 'membership_checkins_manage',
  },
  {
    group: 'prospects',
    permission: 'prospects_create',
  },
  {
    group: 'prospects',
    permission: 'prospects_read',
  },
  {
    group: 'prospects',
    permission: 'prospects_update',
  },
  {
    group: 'prospects',
    permission: 'prospects_delete',
  },
  {
    group: 'prospects',
    permission: 'prospects_tickets_manage',
  },
  {
    group: 'desk',
    permission: 'desk_products_manage',
  },
  {
    group: 'desk',
    permission: 'desk_sales_manage',
  },
  {
    group: 'desk',
    permission: 'locker_keys_manage',
  },
  {
    group: 'desk',
    permission: 'day_notes_manage',
  },
  {
    group: 'desk',
    permission: 'health_screening_release',
  },
  {
    group: 'businessOverview',
    permission: 'business_overview_read',
  },
  {
    group: 'stats',
    permission: 'stats_clients_read',
  },
  {
    group: 'stats',
    permission: 'stats_org_read',
  },
  {
    group: 'stats',
    permission: 'stats_premium_read',
  },
  {
    group: 'premiumContent',
    permission: 'premium_content_manage',
  },
  {
    group: 'questionnaires',
    permission: 'questionnaires_create',
  },
  {
    group: 'questionnaires',
    permission: 'questionnaires_read',
  },
  {
    group: 'questionnaires',
    permission: 'questionnaires_update',
  },
  {
    group: 'questionnaires',
    permission: 'questionnaires_delete',
  },
] as UiPermission[];
